import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TemplateSelector from './components/TemplateSelector';
import PersonalizationForm from './components/PersonalizationForm';
import EmailPreview from './components/EmailPreview';
import EmailStats from './components/EmailStats';

const ComingSoonEmailTemplate = () => {
  const [selectedTemplate, setSelectedTemplate] = useState('welcome-waitlist');
  const [personalizationData, setPersonalizationData] = useState({
    firstName: '',
    familySize: '',
    challenges: '',
    launchTiming: '',
    audienceSegment: ''
  });
  const [activeTab, setActiveTab] = useState('template');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tabs = [
    { id: 'template', name: 'Template', icon: 'Layout', description: 'Choose email template' },
    { id: 'personalize', name: 'Personalize', icon: 'User', description: 'Customize content' },
    { id: 'preview', name: 'Preview', icon: 'Eye', description: 'Review final email' },
    { id: 'analytics', name: 'Analytics', icon: 'BarChart', description: 'Performance metrics' }
  ];

  const handleDownloadHTML = () => {
    // This would typically generate and download the HTML file
    console.log('Downloading HTML template...');
  };

  const handleSendTestEmail = () => {
    // This would typically send a test email
    console.log('Sending test email...');
  };

  return (
    <>
      <Helmet>
        <title>Coming Soon Email Template - Cynizee Marketing Hub</title>
        <meta name="description" content="Branded HTML email templates for waitlist communications with personalization, responsive design, and conversion optimization for family-focused marketing campaigns." />
        <meta name="keywords" content="email template, waitlist email, family marketing, responsive email, HTML email, email personalization" />
        <meta property="og:title" content="Coming Soon Email Template - Cynizee Marketing Hub" />
        <meta property="og:description" content="Professional email templates for family-focused marketing campaigns with personalization and analytics." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Coming Soon Email Template - Cynizee Marketing Hub" />
        <meta name="twitter:description" content="Professional email templates for family-focused marketing campaigns." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-24 pb-12 bg-gradient-to-br from-primary via-secondary to-primary">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Icon name="Mail" size={24} className="text-white" />
                </div>
                <div className="text-left">
                  <h1 className="text-3xl lg:text-4xl font-heading font-bold text-white">
                    Email Template Studio
                  </h1>
                  <p className="text-white/80 text-lg">
                    Branded HTML emails for waitlist communications
                  </p>
                </div>
              </div>
              
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8">
                Create conversion-optimized email templates that maintain brand consistency 
                and build trust with families joining your waitlist.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Button
                  variant="secondary"
                  className="bg-white text-primary hover:bg-white/90"
                  iconName="Download"
                  iconPosition="left"
                  iconSize={18}
                  onClick={handleDownloadHTML}
                >
                  Download Template
                </Button>
                <Button
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  iconName="Send"
                  iconPosition="left"
                  iconSize={18}
                  onClick={handleSendTestEmail}
                >
                  Send Test Email
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <section className="sticky top-16 z-40 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between py-4">
              <nav className="flex space-x-1">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground shadow-soft'
                        : 'text-text-secondary hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span className="hidden sm:inline">{tab?.name}</span>
                  </button>
                ))}
              </nav>
              
              <div className="text-sm text-text-secondary">
                {tabs?.find(tab => tab?.id === activeTab)?.description}
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          {activeTab === 'template' && (
            <div className="space-y-8">
              <TemplateSelector
                selectedTemplate={selectedTemplate}
                onTemplateChange={setSelectedTemplate}
              />
              
              {/* Template Features */}
              <div className="bg-white rounded-xl shadow-soft border border-border p-6">
                <h3 className="text-xl font-heading font-semibold text-foreground mb-4">
                  Template Features & Benefits
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-teal-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon name="Smartphone" size={24} className="text-teal-600" />
                    </div>
                    <h4 className="font-heading font-semibold text-foreground mb-2">
                      Mobile-First Design
                    </h4>
                    <p className="text-text-secondary text-sm">
                      Optimized for mobile devices where 73% of emails are opened, 
                      ensuring perfect rendering across all screen sizes.
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-coral-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon name="Palette" size={24} className="text-coral" />
                    </div>
                    <h4 className="font-heading font-semibold text-foreground mb-2">
                      Brand Consistency
                    </h4>
                    <p className="text-text-secondary text-sm">
                      Maintains Cynizee's visual identity with hexagonal elements, 
                      brand colors, and typography for professional communication.
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gold-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon name="Target" size={24} className="text-gold" />
                    </div>
                    <h4 className="font-heading font-semibold text-foreground mb-2">
                      Conversion Optimized
                    </h4>
                    <p className="text-text-secondary text-sm">
                      Designed with clear CTAs, social proof elements, and 
                      persuasive copy that drives waitlist engagement.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'personalize' && (
            <PersonalizationForm
              personalizationData={personalizationData}
              onDataChange={setPersonalizationData}
            />
          )}

          {activeTab === 'preview' && (
            <EmailPreview
              selectedTemplate={selectedTemplate}
              personalizationData={personalizationData}
            />
          )}

          {activeTab === 'analytics' && (
            <EmailStats />
          )}
        </main>

        {/* Footer CTA */}
        <section className="bg-surface border-t border-border py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
              Ready to Launch Your Email Campaign?
            </h2>
            <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
              Download your customized email template and start building meaningful 
              relationships with families on your waitlist.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Button
                variant="default"
                className="bg-accent hover:bg-accent/90"
                iconName="Download"
                iconPosition="left"
                iconSize={18}
                onClick={handleDownloadHTML}
              >
                Download HTML Template
              </Button>
              <Button
                variant="outline"
                iconName="ExternalLink"
                iconPosition="right"
                iconSize={16}
              >
                View Documentation
              </Button>
            </div>
            
            <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200 max-w-2xl mx-auto">
              <p className="text-sm text-blue-800">
                <Icon name="Info" size={16} className="inline mr-2" />
                <strong>Pro Tip:</strong> Personalized emails with recipient names see 26% higher open rates. 
                Use our personalization features to maximize engagement.
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-primary text-primary-foreground py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-white transform rotate-45 rounded-sm"></div>
                </div>
                <div>
                  <div className="text-xl font-heading font-bold">Cynizee</div>
                  <div className="text-sm opacity-80">Marketing Hub</div>
                </div>
              </div>
              
              <div className="text-center md:text-right">
                <p className="text-sm opacity-90">
                  Transforming family chaos into organized harmony
                </p>
                <p className="text-xs opacity-70 mt-1">
                  © {new Date()?.getFullYear()} Cynizee Marketing Hub. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ComingSoonEmailTemplate;